/**
 * Centralized Event Registry
 * Define all trackable events with type safety and metadata
 */

export interface ActivityEvent {
  type: string;
  category: 'auth' | 'data' | 'security' | 'system';
  requiresAuth: boolean;
  description?: string;
}

export const AUTH_EVENTS = {
  LOGIN_SUCCESS: {
    type: 'login_success',
    category: 'auth' as const,
    requiresAuth: true,
    description: 'User successfully logged in',
  },
  LOGIN_FAILED: {
    type: 'login_failed',
    category: 'auth' as const,
    requiresAuth: false,
    description: 'User login attempt failed',
  },
  LOGOUT: {
    type: 'logout',
    category: 'auth' as const,
    requiresAuth: true,
    description: 'User logged out',
  },
  PASSWORD_RESET_REQUESTED: {
    type: 'password_reset_requested',
    category: 'auth' as const,
    requiresAuth: false,
    description: 'User requested password reset',
  },
  PASSWORD_RESET_COMPLETED: {
    type: 'password_reset_completed',
    category: 'auth' as const,
    requiresAuth: true,
    description: 'User completed password reset',
  },
  PASSWORD_RESET_FAILED: {
    type: 'password_reset_failed',
    category: 'auth' as const,
    requiresAuth: false,
    description: 'Password reset attempt failed',
  },
  OTP_SENT: {
    type: 'otp_sent',
    category: 'auth' as const,
    requiresAuth: false,
    description: 'OTP code sent to user',
  },
  OTP_VERIFIED: {
    type: 'otp_verified',
    category: 'auth' as const,
    requiresAuth: true,
    description: 'User successfully verified OTP',
  },
  OTP_FAILED: {
    type: 'otp_failed',
    category: 'auth' as const,
    requiresAuth: false,
    description: 'OTP verification failed',
  },
  EMAIL_VERIFICATION_SENT: {
    type: 'email_verification_sent',
    category: 'auth' as const,
    requiresAuth: false,
    description: 'Email verification sent',
  },
  PHONE_VERIFICATION_COMPLETED: {
    type: 'phone_verification_completed',
    category: 'auth' as const,
    requiresAuth: true,
    description: 'Phone verification completed',
  },
} as const;

export const SECURITY_EVENTS = {
  RATE_LIMIT_WARNING: {
    type: 'rate_limit_warning',
    category: 'security' as const,
    requiresAuth: false,
    description: 'Rate limit threshold reached',
  },
  RATE_LIMIT_EXCEEDED: {
    type: 'rate_limit_exceeded',
    category: 'security' as const,
    requiresAuth: false,
    description: 'Rate limit exceeded',
  },
  INVALID_REDIRECT: {
    type: 'invalid_redirect',
    category: 'security' as const,
    requiresAuth: false,
    description: 'Invalid redirect attempt detected',
  },
  CSRF_TOKEN_INVALID: {
    type: 'csrf_token_invalid',
    category: 'security' as const,
    requiresAuth: false,
    description: 'CSRF token validation failed',
  },
  SESSION_EXPIRED: {
    type: 'session_expired',
    category: 'security' as const,
    requiresAuth: false,
    description: 'User session expired',
  },
} as const;

export const DATA_EVENTS = {
  PROFILE_UPDATED: {
    type: 'profile_updated',
    category: 'data' as const,
    requiresAuth: true,
    description: 'User profile updated',
  },
  USER_CREATED: {
    type: 'user_created',
    category: 'data' as const,
    requiresAuth: true,
    description: 'New user created by admin',
  },
  USER_DELETED: {
    type: 'user_deleted',
    category: 'data' as const,
    requiresAuth: true,
    description: 'User deleted by admin',
  },
  USER_ROLE_UPDATED: {
    type: 'user_role_updated',
    category: 'data' as const,
    requiresAuth: true,
    description: 'User role updated by admin',
  },
  EMAIL_UPDATED: {
    type: 'email_updated',
    category: 'data' as const,
    requiresAuth: true,
    description: 'User email updated by admin',
  },
} as const;

export type AuthEventType = typeof AUTH_EVENTS[keyof typeof AUTH_EVENTS];
export type SecurityEventType = typeof SECURITY_EVENTS[keyof typeof SECURITY_EVENTS];
export type DataEventType = typeof DATA_EVENTS[keyof typeof DATA_EVENTS];
export type AllEventType = AuthEventType | SecurityEventType | DataEventType;
